import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useMatches, useNavigate, useOutletContext, useRevalidator, useRouteLoaderData } from 'react-router-dom';

import styles from '../../../Info.module.scss';

import { Button } from '@progress/kendo-react-buttons';
import { Form, FormRenderProps } from '@progress/kendo-react-form';

import { ConnectedServiceType, IConnectedService, ICustomer, ILookupValue } from '../../../../model';
import { caretAltLeftIcon } from '@progress/kendo-svg-icons';
import { SvgIcon } from '@progress/kendo-react-common';
import billingIcon from '../../../../assets/billingIcon.svg';
import invoicesIcon from '../../../../assets/invoicesIcon.svg';
import settingsIcon from '../../../../assets/settingsIcon.svg';
import assetsIcon from '../../../../assets/assetsIcon.svg';
import detailsIcon from '../../../../assets/detailsIcon.svg';
import locationsIcon from '../../../../assets/locationsIcon.svg';
import featureIcon from '../../../../assets/featureIcon.svg';
import { UploadFileInfo } from '@progress/kendo-react-upload';
import { customersService, imagesService, notifications } from '../../../../services';
import { ICustomerUpsert } from '../../../../model/api/customers/customer-upsert';
import { PermissionsContextType, usePermissions } from '../../../../components/auth/PermissionsProvider';
import { hasNexCity, hasNexPark, hasNexResponse } from '../../../../model/api/nexParkInstance/instancesChecker';
import SidebarLink from '../../../../components/common/sidebar-link/SidebarLink';

interface INavLink {
  name: string;
  svgIcon: any;
  path?: string;
  enabled?: boolean;
  links?: INavLink[];
}

interface RouteHandle {
  tabName?: string;
  crumb?: (data: any) => string;
}

const INNER_TABS_WITHOUT_CRUD_CONTROLS = ['Links', 'Contacts'];

const createMainLinks = (customer: ICustomer, hasBillingViewAccess: boolean) => {
  const showLocationsTab = () => {
    const isNotConnectedToAnyService = !customer.connectedServices || customer.connectedServices.length === 0;

    if (isNotConnectedToAnyService) {
      return false;
    }

    return !hasNexCity(customer);
  };

  const mainNavLinks: INavLink[] = [
    { name: 'Details', path: 'Details', svgIcon: detailsIcon, enabled: true },
    { name: 'Locations', path: 'Locations', svgIcon: locationsIcon, enabled: showLocationsTab() },
    {
      name: 'NexPark Features',
      path: 'Features',
      svgIcon: featureIcon,
      enabled: hasNexPark(customer),
    },
    {
      name: 'Billing',
      svgIcon: billingIcon,
      enabled: hasBillingViewAccess && (hasNexPark(customer) || hasNexResponse(customer)),
      links: [
        { name: 'Invoices', path: 'billing-invoices', svgIcon: invoicesIcon, enabled: true },
        { name: 'Settings', path: 'billing-settings', svgIcon: settingsIcon, enabled: true },
      ],
    },
    { name: 'Assets', path: 'Assets', svgIcon: assetsIcon, enabled: true },
  ];
  return mainNavLinks.filter((link) => link.enabled);
};

export const CustomerInfo = () => {
  const { hasEditAccess, hasBillingViewAccess } = usePermissions();
  const permissions = useOutletContext<PermissionsContextType>();
  const customerFromRoute = useRouteLoaderData('customer-info') as ICustomer;
  const matches = useMatches();
  const revalidator = useRevalidator();
  const currentTabMatch = matches[matches.length - 1];
  const handle = currentTabMatch?.handle as RouteHandle | undefined;
  const currentTab = handle?.tabName ?? 'Details';
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { edit: boolean } | null;

  const isNew = customerFromRoute.id === 0;

  const [activeTab, setActiveTab] = useState(currentTab);
  const [customer, setCustomer] = useState<ICustomer>(customerFromRoute);
  const [edit, setEdit] = useState(state ? state.edit : false);
  const [busy, setBusy] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [originalCustomer, setOriginalCustomer] = useState<ICustomer | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [innerTab, setInnerTab] = useState<string>('General');

  const handleEditClick = () => {
    setOriginalCustomer(customer);
    setEdit(true);
  };

  const handleCancelForm = () => {
    if (isNew) {
      navigate('/customers');
    } else {
      if (originalCustomer) {
        setOriginalCustomer(originalCustomer);
      }
      setIsFormModified(false);
      setEdit(false);
    }
  };

  const handleFormSubmit = useCallback(
    async (dataItem: { [name: string]: unknown }) => {
      let imageId = dataItem['imageId'] as number | null;

      if (dataItem['logo']) {
        const logo = dataItem['logo'] as UploadFileInfo;
        const file = logo.getRawFile ? logo.getRawFile() : undefined;
        if (file) {
          if (!imageId) {
            imageId = (await imagesService.create(file)) as number;
          } else {
            await imagesService.update(imageId, file);
          }
        }
        delete dataItem['logo'];
      } else if (dataItem['logo'] === null) {
        imageId = null;
      }

      dataItem['imageId'] = imageId;
      dataItem['industrySectorId'] = (dataItem['industrySector'] as ILookupValue)?.id;

      if ((dataItem['connectedServices'] as ILookupValue[])?.some((x) => x.id === ConnectedServiceType.NexCity)) {
        dataItem['cityId'] = (dataItem['city'] as ILookupValue)?.id;
      }

      if ((dataItem['connectedServices'] as ILookupValue[])?.some((x) => x.id === ConnectedServiceType.NexPark)) {
        dataItem['nexParkInstanceId'] = (dataItem['nexParkInstance'] as ILookupValue)?.id;
      }

      if ((dataItem['connectedServices'] as ILookupValue[])?.some((x) => x.id === ConnectedServiceType.NexResponse)) {
        if (Object.hasOwn(dataItem, 'nexResponseRegion'))
          dataItem['nexResponseRegionId'] = (dataItem['nexResponseRegion'] as ILookupValue)?.id;
      }

      dataItem['connectedServiceIds'] = (dataItem['connectedServices'] as ILookupValue[])
        ?.filter((x) => x?.id !== undefined)
        ?.map((x) => x.id);

      const moduleIds = (dataItem['connectedServices'] as IConnectedService[])
        ?.filter((x) => x?.modules?.length > 0)
        ?.map((x) => x.modules.map((y) => y.id));

      dataItem['moduleIds'] = moduleIds?.length ? moduleIds.reduce((x, y) => x.concat(y)) : [];

      if (((dataItem['snapshotsBucket'] as string) || '').trim() === '') {
        dataItem['snapshotsBucket'] = null;
      }

      try {
        setBusy(true);
        if (customer.id) {
          await customersService.update(customer.id, dataItem as unknown as ICustomerUpsert).then(() => {
            notifications.success('The customer was successfully updated.');
            const newCustomerData = {
              ...dataItem,
              id: customer.id,
            } as ICustomer;
            setCustomer(newCustomerData);
            revalidator.revalidate();
          });
        } else {
          await customersService.create(dataItem as unknown as ICustomerUpsert).then((newId: any) => {
            const newCustomerData = {
              ...dataItem,
              id: newId,
            } as ICustomer;
            setCustomer(newCustomerData);
            navigate(`/customers/${newId}/details`);
            notifications.success('The customer was successfully created.');
          });
        }
      } finally {
        setBusy(false);
        setEdit(false);
      }
    },
    [customer, navigate],
  );

  useEffect(() => {
    setCustomer(customerFromRoute);
  }, [customerFromRoute]);

  const mainNavLinks = useMemo(() => createMainLinks(customer, hasBillingViewAccess), [customer]);

  return (
    <div className={styles.newInfoPage}>
      <nav className={`${styles.mainSidebar} ${collapsed ? styles.collapsed : ''}`}>
        <button className={styles.sidebarToggle} onClick={() => setCollapsed(!collapsed)}>
          <SvgIcon icon={caretAltLeftIcon} style={{ transform: collapsed ? 'rotate(180deg)' : 'none' }} />
        </button>
        <ul>
          {mainNavLinks.map((link) => (
            <li key={link.name}>
              <SidebarLink
                link={link}
                basePath={`/customers/${customer.id}`}
                isNew={customer.id === 0}
                collapsed={collapsed}
                isExpanded={expandedGroups[link.name]}
                rootLinkName={'Details'}
                onToggle={() => setExpandedGroups((p) => ({ ...p, [link.name]: !p[link.name] }))}
                onActive={setActiveTab}
              />
            </li>
          ))}
        </ul>
      </nav>

      <main className={styles.contentArea}>
        <header className={styles.contentHeader}>
          <Button className={styles.crumbButtonLink} fillMode='link' onClick={() => navigate('/customers')}>
            <SvgIcon icon={caretAltLeftIcon} />
            <span>Customers</span>
          </Button>

          {activeTab === 'Details' && !INNER_TABS_WITHOUT_CRUD_CONTROLS.includes(innerTab) && (
            <div className={styles.headerActions}>
              {edit ? (
                <>
                  <Button disabled={busy} type='button' onClick={() => handleCancelForm()}>
                    Cancel
                  </Button>
                  <Button themeColor='primary' type='submit' form='main-customer-form' disabled={busy || !isFormModified}>
                    Save
                  </Button>
                </>
              ) : (
                hasEditAccess && (
                  <Button type='button' onClick={() => handleEditClick()}>
                    Edit
                  </Button>
                )
              )}
            </div>
          )}
        </header>

        <div className={styles.tabContentWrapper}>
          <Form
            onSubmit={handleFormSubmit}
            key={`form-${customer?.id}`}
            initialValues={customer}
            render={(formRenderProps: FormRenderProps) => {
              if (formRenderProps.modified !== isFormModified) {
                setIsFormModified(formRenderProps.modified);
              }

              return (
                <form id='main-customer-form' onSubmit={formRenderProps.onSubmit}>
                  <Outlet
                    context={{
                      formRenderProps,
                      edit,
                      isNew,
                      onInnerTabChange: setInnerTab,
                      ...permissions,
                    }}
                  />
                </form>
              );
            }}
          />
        </div>
      </main>
    </div>
  );
};

type FormOutletContext = PermissionsContextType & {
  formRenderProps: any;
  edit: boolean;
  onInnerTabChange: (tab: string) => void;
  isNew?: boolean;
};

export function useFormOutletContext() {
  return useOutletContext<FormOutletContext>();
}

export default CustomerInfo;
